import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { useNotification } from './index';

/**
 * A reusable CSV import flow component with column mapping functionality
 * 
 * @param {Object} props Component props
 * @param {string} props.entityType Type of entity to import (leads, deals)
 * @param {function} props.onImportComplete Callback when import is finished
 * @param {boolean} props.isSimplifiedMode Use simplified interface (for onboarding)
 * @param {Object} props.customFieldMappings Additional field mappings specific to this entity type
 */
const CsvImportFlow = ({ 
  entityType = 'leads', 
  onImportComplete, 
  isSimplifiedMode = false,
  customFieldMappings = {}
}) => {
  const { showNotification } = useNotification();
  const fileInputRef = useRef(null);
  
  // Main states for the import flow
  const [currentStep, setCurrentStep] = useState('upload'); // upload, preview, mapping, importing, complete
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreviewData, setCsvPreviewData] = useState({ headers: [], rows: [], totalRows: 0 });
  const [mappings, setMappings] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const [previewCollapsed, setPreviewCollapsed] = useState(false);
  const [importStatus, setImportStatus] = useState({
    loading: false,
    success: null,
    message: '',
    totalRows: 0,
    importedCount: 0,
    newCount: 0,
    updatedCount: 0,
    errors: []
  });

  // Define standard field mappings for each entity type
  const standardFieldMappings = {
    leads: {
      'email': { label: 'Email', required: false, description: 'Lead email address (auto-generated if not provided)' },
      'name': { label: 'Name', required: false, description: 'Full name' },
      'first_name': { label: 'First Name', required: false, description: 'First name' },
      'last_name': { label: 'Last Name', required: false, description: 'Last name' },
      'phone': { label: 'Phone', required: false, description: 'Phone number' },
      'company': { label: 'Company', required: false, description: 'Company name' },
      'original_source': { label: 'Original Source', required: false, description: 'How the lead was initially acquired' },
      'latest_source': { label: 'Latest Source', required: false, description: 'Most recent marketing touchpoint' },
      'status': { label: 'Status', required: false, description: 'Lead status (New, Contacted, etc.)' },
      'country': { label: 'Country', required: false, description: 'Lead\'s country' },
      'add_to_notes': { label: 'Notes', required: false, description: 'Add this field data to lead notes (allows multiple fields)' },
      'subscribed': { label: 'Subscribed', required: false, description: 'Marketing email subscription status' },
      'created_at': { label: 'Create Date', required: false, description: 'Date when the lead was created' }
    },
    deals: {
      'title': { label: 'Title', required: true, description: 'Deal title or name' },
      'value': { label: 'Value', required: false, description: 'Deal monetary value' },
      'currency': { label: 'Currency', required: false, description: 'Deal currency (USD, EUR, etc.)' },
      'stage': { label: 'Stage', required: false, description: 'Pipeline stage name' },
      'close_date': { label: 'Close Date', required: false, description: 'Expected close date' },
      'lead_email': { label: 'Lead Email', required: false, description: 'Email of associated lead' },
      'lead_name': { label: 'Lead Name', required: false, description: 'Name of associated lead' },
      'description': { label: 'Notes', required: false, description: 'Deal notes and details' },
      'product': { label: 'Product', required: false, description: 'Product or service name' },
      'created_at': { label: 'Create Date', required: false, description: 'Date when the deal was created' }
    }
  };
  
  // Combine standard mappings with custom mappings
  const fieldOptions = {
    ...standardFieldMappings[entityType],
    ...customFieldMappings
  };

  // Initialize default mappings when headers are loaded
  useEffect(() => {
    if (csvPreviewData.headers.length > 0 && Object.keys(mappings).length === 0) {
      // Generate auto-mappings based on header similarity
      const autoMappings = {};
      
      // Track which fields have already been mapped to avoid duplicates
      const usedFields = new Set();
      
      csvPreviewData.headers.forEach(header => {
        // Find the best match among field options
        const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');
        
        let bestMatch = null;
        let bestScore = 0;
        
        // Smart mapping rules - exact matches get priority
        const smartMappings = {
          // Company/Business variations
          'businessname': 'company',
          'companyname': 'company',
          'organization': 'company',
          'firm': 'company',
          'business': 'company',
          
          // Location/Address variations
          'location': 'address',
          'addr': 'address',
          'street': 'address',
          'city': 'address',
          'fulladdress': 'address',
          
          // Email variations
          'emailaddress': 'email',
          'mail': 'email',
          'secondaryemail': 'secondary_email',
          'alternativeemail': 'secondary_email',
          'email2': 'secondary_email',
          
          // Phone variations
          'phonenumber': 'phone',
          'telephone': 'phone',
          'mobile': 'phone',
          'cell': 'phone',
          
          // Name variations
          'fullname': 'name',
          'firstname': 'first_name',
          'lastname': 'last_name',
          'surname': 'last_name',
          'givenname': 'first_name',
          'familyname': 'last_name',
          
          // Role/Position variations - be more specific to avoid conflicts
          'jobtitle': 'role',
          'position': 'role',
          'title': 'role',
          'occupation': 'role',
          'currentrole': 'role',
          
          // Source variations
          'source': 'original_source',
          'leadsource': 'original_source',
          'originalsource': 'original_source',
          'latestsource': 'latest_source',
          
          // Other common variations
          'notes': 'add_to_notes',
          'comments': 'add_to_notes',
          'description': 'add_to_notes',
          'callattemptsmax': 'call_attempts',
          'attempts': 'call_attempts',
          'assigned': 'assigned_to',
          'assignedto': 'assigned_to',
          'owner': 'assigned_to'
        };
        
        // Check for smart mappings first
        if (smartMappings[normalizedHeader] && fieldOptions[smartMappings[normalizedHeader]]) {
          const potentialMatch = smartMappings[normalizedHeader];
          // Only use this mapping if the field hasn't been used yet
          // Exception: 'add_to_notes' can be used multiple times
          if (!usedFields.has(potentialMatch) || potentialMatch === 'add_to_notes') {
            bestMatch = potentialMatch;
            bestScore = 1;
          }
        }
        
        // If no smart mapping or field already used, try fallback algorithm
        if (!bestMatch) {
          Object.entries(fieldOptions).forEach(([fieldKey, fieldData]) => {
            // Skip if this field is already mapped (except 'add_to_notes' which allows multiple)
            if (usedFields.has(fieldKey) && fieldKey !== 'add_to_notes') return;
            
            // Check different variations of the field name
            const variations = [
              fieldData.label.toLowerCase().replace(/[^a-z0-9]/g, ''),
              fieldKey.toLowerCase().replace(/[^a-z0-9]/g, '')
            ];
            
            variations.forEach(variation => {
              // Exact match gets highest score
              if (normalizedHeader === variation) {
                if (1 > bestScore) {
                  bestMatch = fieldKey;
                  bestScore = 1;
                }
              } 
              // Partial match gets lower score
              else if (normalizedHeader.includes(variation) || variation.includes(normalizedHeader)) {
                const score = 0.6; // Lowered from 0.8 to prefer smart mappings
                if (score > bestScore) {
                  bestScore = score;
                  bestMatch = fieldKey;
                }
              }
            });
          });
        }
        
        // If good match found (score > 0.5), use it and mark field as used
        if (bestScore > 0.5 && bestMatch) {
          autoMappings[header] = bestMatch;
          // Don't mark 'add_to_notes' as used since it allows multiple mappings
          if (bestMatch !== 'add_to_notes') {
            usedFields.add(bestMatch);
          }
        } else {
          autoMappings[header] = 'ignore';
        }
      });
      
      setMappings(autoMappings);
    }
  }, [csvPreviewData.headers, fieldOptions]);

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  // Process the file (common function for both drop and select)
  const processFile = (file) => {
    // Check if it's a CSV file
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showNotification('Please upload a CSV file', 'error');
      return;
    }
    
    setCsvFile(file);
    
    // Read the file for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const csv = event.target.result;
      parseCSVPreview(csv);
    };
    reader.readAsText(file);
    
    // Move to preview step
    setCurrentStep('preview');
  };

  // Handle file selection
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      processFile(e.target.files[0]);
    }
  };
  
  // Parse the first few rows of CSV for preview
  const parseCSVPreview = (csvText) => {
    // First, normalize line endings
    const normalizedText = csvText.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedText.split('\n');
    if (lines.length === 0) return;
    
    // Detect delimiter by counting commas and semicolons in the header
    const headerLine = lines[0];
    const commaCount = (headerLine.match(/,/g) || []).length;
    const semicolonCount = (headerLine.match(/;/g) || []).length;
    const delimiter = semicolonCount > commaCount ? ';' : ',';
    
    console.log(`Detected delimiter: '${delimiter}' (commas: ${commaCount}, semicolons: ${semicolonCount})`);
    
    // Simple CSV parser that properly handles quoted fields
    const parseCSVLine = (line, delimiter) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          // Handle quotes - toggle quote state
          inQuotes = !inQuotes;
        } else if (char === delimiter && !inQuotes) {
          // Found delimiter outside quotes - end current field
          result.push(current.trim());
          current = '';
        } else {
          // Regular character - add to current field
          current += char;
        }
      }
      
      // Add the last field
      result.push(current.trim());
      return result;
    };
    
    // Parse header and filter out empty headers
    const headers = parseCSVLine(lines[0], delimiter)
      .map(h => h.replace(/^"(.+)"$/, '$1')) // Remove surrounding quotes
      .filter(h => h !== ''); // Remove empty headers
    
    // Calculate total row count (excluding header and empty lines)
    const totalRows = lines.slice(1).filter(line => line && line.trim() !== '').length;
    
    // Parse up to 5 rows for preview
    const rows = [];
    const previewRowCount = Math.min(5, lines.length - 1);
    
    for (let i = 1; i <= previewRowCount; i++) {
      if (!lines[i] || lines[i].trim() === '') continue;
      
      // Use proper CSV parsing for quoted fields
      const rowValues = parseCSVLine(lines[i], delimiter);
      
      // Process each value to clean up quotes and trim
      const processedRow = rowValues.map(value => {
        // Remove surrounding quotes if present
        if (value.startsWith('"') && value.endsWith('"')) {
          return value.substring(1, value.length - 1);
        }
        return value.trim();
      });
      
      // Only keep as many values as we have headers
      const rowData = processedRow.slice(0, headers.length);
      
      // Ensure row has the same number of columns as headers
      while (rowData.length < headers.length) {
        rowData.push('');
      }
      
      rows.push(rowData);
    }
    
    setCsvPreviewData({ headers, rows, delimiter, totalRows });
  };
  
  // Update the mapping for a specific CSV column
  const updateMapping = (csvHeader, crmField) => {
    setMappings({
      ...mappings,
      [csvHeader]: crmField
    });
  };
  
  // Validate mappings before import
  const validateMappings = () => {
    // Check required fields are mapped
    const requiredFields = Object.entries(fieldOptions)
      .filter(([_, data]) => data.required)
      .map(([field, _]) => field);
    
    const mappedFields = Object.values(mappings);
    const missingRequiredFields = requiredFields.filter(field => !mappedFields.includes(field));
    
    if (missingRequiredFields.length > 0) {
      const missingFieldLabels = missingRequiredFields.map(field => fieldOptions[field].label).join(', ');
      showNotification(`Missing required field mappings: ${missingFieldLabels}`, 'error');
      return false;
    }
    
    return true;
  };
  
  // Proceed to mapping step
  const handleProceedToMapping = () => {
    setCurrentStep('mapping');
  };
  
  // Start the import process
  const handleStartImport = async () => {
    if (!validateMappings()) {
      return;
    }
    
    setCurrentStep('importing');
    setImportStatus({
      ...importStatus,
      loading: true,
      message: 'Processing your CSV file...',
      errors: []
    });
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication error. Please log in again.');
      }
      
      // Create form data for file upload
      const formData = new FormData();
      formData.append('csv', csvFile);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('entityType', entityType);
      
      // Add the preview data delimiter to ensure consistent parsing
      if (csvPreviewData.delimiter) {
        formData.append('delimiter', csvPreviewData.delimiter);
      }
      
      // Send the import request
      const response = await axios.post(
        `/api/${entityType}/import/csv-with-mapping`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'x-auth-token': token
          }
        }
      );
      
      // Update status with results
      setImportStatus({
        loading: false,
        success: true,
        message: response.data.message || `Successfully imported ${response.data.importedCount} ${entityType}`,
        totalRows: response.data.totalRows || 0,
        importedCount: response.data.importedCount || 0,
        newCount: response.data.newCount || 0,
        updatedCount: response.data.updatedCount || 0,
        errors: response.data.errors || []
      });
      
      // Move to complete step
      setCurrentStep('complete');
      
      // Notify success
      showNotification(`Successfully imported ${response.data.importedCount} ${entityType}`, 'success');
      
      // Call completion callback if provided
      if (onImportComplete) {
        onImportComplete({
          success: true,
          importedCount: response.data.importedCount,
          newCount: response.data.newCount,
          updatedCount: response.data.updatedCount
        });
      }
    } catch (error) {
      console.error('Import error:', error);
      
      setImportStatus({
        loading: false,
        success: false,
        message: error.response?.data?.message || 'Failed to import CSV file',
        errors: error.response?.data?.errors || [error.message]
      });
      
      // Stay on importing step but show errors
      showNotification(error.response?.data?.message || 'Failed to import CSV file', 'error');
    }
  };
  
  // Reset the import flow
  const handleReset = () => {
    setCsvFile(null);
    setCsvPreviewData({ headers: [], rows: [], totalRows: 0 });
    setMappings({});
    setImportStatus({
      loading: false,
      success: null,
      message: '',
      totalRows: 0,
      importedCount: 0,
      newCount: 0,
      updatedCount: 0,
      errors: []
    });
    setCurrentStep('upload');
  };
  
  // Render the file upload step
  const renderUploadStep = () => (
    <div className="csv-import-upload">
      <div 
        className={`text-center py-10 border-2 border-dashed ${dragActive 
          ? 'border-blue-400 bg-blue-50' 
          : 'border-gray-300 bg-gray-50'
        } rounded-lg transition-colors duration-200`}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={handleDrop}
      >
        <input
          type="file"
          accept=".csv"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="hidden"
        />
        
        <svg xmlns="http://www.w3.org/2000/svg" className={`mx-auto h-12 w-12 ${dragActive ? 'text-blue-500' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
        
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          {dragActive ? 'Drop your CSV file here' : 'Upload a CSV file'}
        </h3>
        <p className="mt-1 text-xs text-gray-500">
          {dragActive ? 'Release to upload' : 'Drag and drop or click to browse'}
        </p>
        
        <div className="mt-6">
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => fileInputRef.current?.click()}
          >
            Select CSV File
          </button>
        </div>
      </div>
      
      <div className="mt-6 bg-blue-50 rounded-md p-4 text-sm text-blue-700">
        <p><strong>CSV Import Tips:</strong></p>
        <ul className="list-disc pl-5 mt-2">
          <li>Make sure your CSV file has a header row with column names</li>
          <li>Files exported from most spreadsheet applications (Excel, Google Sheets) work well</li>
          <li>Only {entityType === 'leads' ? 'Email' : 'Title'} is required</li>
          <li>You'll be able to map your columns to the appropriate fields in the next step</li>
        </ul>
      </div>
    </div>
  );
  
  // Render the preview step
  const renderPreviewStep = () => (
    <div className="csv-import-preview">
      <h3 className="text-lg font-medium mb-2">CSV Preview</h3>
      <p className="text-sm text-gray-600 mb-4">
        We've detected {csvPreviewData.headers.length} columns in your CSV file. 
        You'll be able to map these to the appropriate fields in the next step.
      </p>
      
      <div className="overflow-x-auto border border-gray-200 rounded-md mb-6">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {csvPreviewData.headers.map((header, index) => (
                <th key={index} className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="max-w-32 truncate" title={header}>
                    {header}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 text-sm">
            {csvPreviewData.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-3 py-2 text-gray-500">
                    <div className="max-w-32 truncate" title={cell}>
                      {cell || <span className="text-gray-400 italic">empty</span>}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <div className="flex justify-between">
        <button
          type="button"
          className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          onClick={handleReset}
        >
          Back
        </button>
        
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={handleProceedToMapping}
        >
          Continue to Mapping
        </button>
      </div>
    </div>
  );
  
  // Render the mapping step
  const renderMappingStep = () => (
    <div className="csv-import-mapping">
      <h3 className="text-lg font-medium mb-2">Map CSV Columns</h3>
      <p className="text-sm text-gray-600 mb-4">
        Match your CSV columns to the appropriate {entityType === 'leads' ? 'lead' : 'deal'} fields.
        {entityType === 'leads' 
          ? ' Email is required.'
          : ' Title is required.'}
      </p>
      
      <div className="space-y-3 mb-6">
        <div className="bg-yellow-50 border border-yellow-100 rounded-md p-3 text-sm text-yellow-800">
          <strong>Tip:</strong> Select "Do not import" for any columns you want to skip.
        </div>
        
        <div className="bg-blue-50 border border-blue-100 rounded-md p-3 text-sm text-blue-800">
          <strong>Important:</strong> When updating existing records, only fields you map will be updated, and only if they contain data. Empty cells in mapped columns will not overwrite your existing data.
        </div>
      </div>

      {/* CSV Preview Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-md font-medium text-gray-900">CSV Preview</h4>
          <button
            type="button"
            onClick={() => setPreviewCollapsed(!previewCollapsed)}
            className="flex items-center text-sm text-gray-600 hover:text-gray-900 focus:outline-none"
          >
            {previewCollapsed ? (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                Show Preview
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
                Hide Preview
              </>
            )}
          </button>
        </div>
        
        {!previewCollapsed && (
          <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {csvPreviewData.headers.map((header, index) => (
                      <th
                        key={index}
                        className="px-3 py-2 text-left font-medium text-gray-700 border-r border-gray-200 last:border-r-0"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {csvPreviewData.rows.slice(0, 3).map((row, rowIndex) => (
                    <tr key={rowIndex} className="hover:bg-gray-50">
                      {row.map((cell, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="px-3 py-2 text-gray-900 border-r border-gray-200 last:border-r-0"
                        >
                          <div className="max-w-32 truncate" title={cell}>
                            {cell || <span className="text-gray-400 italic">empty</span>}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {csvPreviewData.totalRows > 3 && (
              <div className="px-3 py-2 bg-gray-50 text-xs text-gray-500 text-center border-t border-gray-200">
                Showing first 3 rows of {csvPreviewData.totalRows} total rows
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Column Mapping Section */}
      <h4 className="text-md font-medium text-gray-900 mb-3">Column Mapping</h4>
      <div className="space-y-4 max-h-96 overflow-y-auto pr-2 mb-6">
        {csvPreviewData.headers.map((header, index) => (
          <div key={index} className="flex items-center bg-gray-50 p-3 rounded-md">
            <div className="flex-1 font-medium text-gray-700">{header}</div>
            <div className="flex-none mx-2 text-gray-400">→</div>
            <div className="flex-1">
              <select
                value={mappings[header] || 'ignore'}
                onChange={(e) => updateMapping(header, e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              >
                <option value="ignore">Do not import</option>
                {Object.entries(fieldOptions).map(([field, data]) => (
                  <option key={field} value={field}>
                    {data.label}{data.required ? ' (Required)' : ''}
                  </option>
                ))}
              </select>
              {mappings[header] !== 'ignore' && fieldOptions[mappings[header]] && (
                <p className="mt-1 text-xs text-gray-500">{fieldOptions[mappings[header]].description}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {/* Notes Preview Section */}
      {(() => {
        const addToNotesFields = Object.entries(mappings).filter(([_, field]) => field === 'add_to_notes');
        if (addToNotesFields.length > 0) {
          return (
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-md p-4">
              <h4 className="text-sm font-medium text-blue-900 mb-2">
                Notes Preview ({addToNotesFields.length} field{addToNotesFields.length > 1 ? 's' : ''} will be added to notes)
              </h4>
              <div className="text-xs text-blue-800">
                <div className="font-medium mb-1">[Additional Fields from CSV Import]</div>
                {addToNotesFields.map(([csvColumn], index) => (
                  <div key={index} className="ml-2">
                    {csvColumn}: [data from CSV]
                  </div>
                ))}
              </div>
              <p className="text-xs text-blue-700 mt-2">
                These fields will be formatted and added to each lead's notes section.
              </p>
            </div>
          );
        }
        return null;
      })()}
      
      <div className="flex justify-between">
        <button
          type="button"
          className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
          onClick={() => setCurrentStep('preview')}
        >
          Back
        </button>
        
        <button
          type="button"
          className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          onClick={handleStartImport}
        >
          Start Import
        </button>
      </div>
    </div>
  );
  
  // Render the importing step
  const renderImportingStep = () => (
    <div className="csv-import-importing">
      <h3 className="text-lg font-medium mb-2">Importing Your Data</h3>
      
      {importStatus.loading ? (
        <div className="py-8 text-center">
          <svg className="animate-spin h-10 w-10 text-blue-600 mx-auto mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-gray-600">{importStatus.message || 'Processing your data...'}</p>
        </div>
      ) : (
        <>
          <div className={`p-4 mb-4 rounded-md ${
            importStatus.success 
              ? 'bg-green-50 text-green-700 border border-green-200' 
              : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            <p className="font-medium">{importStatus.message}</p>
            
            {importStatus.success && (
              <div className="mt-2 text-sm">
                <p>Total rows processed: {importStatus.totalRows}</p>
                <p>Successfully imported: {importStatus.importedCount}</p>
                <p>New {entityType}: {importStatus.newCount}</p>
                <p>Updated {entityType}: {importStatus.updatedCount}</p>
              </div>
            )}
          </div>
          
          {importStatus.errors && importStatus.errors.length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-2">Errors ({importStatus.errors.length})</h4>
              <div className="max-h-40 overflow-y-auto bg-gray-50 p-3 rounded-md text-sm">
                <ul className="list-disc list-inside space-y-1">
                  {importStatus.errors.map((error, index) => (
                    <li key={index} className="text-red-600">{error}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          
          <div className="flex justify-between">
            <button
              type="button"
              className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
              onClick={handleReset}
            >
              Start Over
            </button>
            
            {importStatus.success && (
              <button
                type="button"
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                onClick={() => setCurrentStep('complete')}
              >
                Continue
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
  
  // Render the complete step
  const renderCompleteStep = () => (
    <div className="csv-import-complete">
      <div className="text-center py-8">
        <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        
        <h3 className="mt-2 text-lg font-semibold text-gray-900">Import Complete!</h3>
        <p className="mt-1 text-gray-500">
          Successfully imported {importStatus.importedCount} {entityType}.
          {importStatus.newCount > 0 && ` Added ${importStatus.newCount} new records.`}
          {importStatus.updatedCount > 0 && ` Updated ${importStatus.updatedCount} existing records.`}
        </p>
        
        <div className="mt-6">
          <button
            type="button"
            className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-md shadow-sm hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 mr-2"
            onClick={handleReset}
          >
            Import Another File
          </button>
          
          <button
            type="button"
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            onClick={() => {
              if (onImportComplete) {
                onImportComplete({
                  success: true,
                  importedCount: importStatus.importedCount,
                  newCount: importStatus.newCount,
                  updatedCount: importStatus.updatedCount,
                  isComplete: true
                });
              }
            }}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
  
  // Render the current step
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'upload':
        return renderUploadStep();
      case 'preview':
        return renderPreviewStep();
      case 'mapping':
        return renderMappingStep();
      case 'importing':
        return renderImportingStep();
      case 'complete':
        return renderCompleteStep();
      default:
        return renderUploadStep();
    }
  };
  
  return (
    <div className="csv-import-flow">
      {/* Progress indicator */}
      <div className="mb-6 pb-4 border-b border-gray-200">
        <div className="flex items-center">
          {/* First, create a container with steps */}
          <div className="w-full">
            <div className="relative">
              {/* Background line */}
              <div className="absolute left-0 top-4 w-full h-0.5 bg-gray-200"></div>
              
              {/* Active progress line */}
              <div 
                className="absolute left-0 top-4 h-0.5 bg-green-500 transition-all duration-500"
                style={{
                  width: `${
                    Math.max(0, ['upload', 'preview', 'mapping', 'importing', 'complete'].indexOf(currentStep) * 25)
                  }%`
                }}
              ></div>
              
              {/* Step indicators */}
              <div className="relative flex justify-between">
                {['upload', 'preview', 'mapping', 'importing', 'complete'].map((step, index) => (
                  <div key={step} className="flex flex-col items-center">
                    <div 
                      className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${
                        currentStep === step
                          ? 'bg-blue-600 text-white'
                          : ['complete', 'importing'].includes(currentStep) && 
                            index < ['upload', 'preview', 'mapping', 'importing', 'complete'].indexOf(currentStep)
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {index + 1}
                    </div>
                    <div className="mt-2 text-xs font-medium text-gray-700 capitalize">{step}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Current step content */}
      {renderCurrentStep()}
    </div>
  );
};

CsvImportFlow.propTypes = {
  entityType: PropTypes.oneOf(['leads', 'deals']),
  onImportComplete: PropTypes.func,
  isSimplifiedMode: PropTypes.bool,
  customFieldMappings: PropTypes.object
};

export default CsvImportFlow;